import type { ConstructionSiteStatus, Prisma } from "@prisma/client";
import { clientSocietySelector, clientUserSelector } from "../clients/clients.vars";
import { constructionSiteOfferSelector } from "./constructionSitesOffers.vars";
import { ConstructionSiteDocumentType } from "./constructionSites.internal.vars";
import { brokerSocietyBaseSelector } from "../societies/societies.vars";

export const clientTypes = ["USER", "SOCIETY"] as const;

export const actionTypes = ["CONSTRUCTION", "RENOVATION", "EXTENSION", "RENOVATION_OR_EXTENSION", "SYNDIC"] as const;

export const constructionSiteStatuses = [
	"NEW",
	"IN_PROGRESS",
	"TO_ANALYZE",
  	"VALIDATED",
  	"REFUSED",
	"TO_RECEIVE",
	"RECEIVED",
] as const;

export const gfaStatuses = [
	"NEW",
  	"TO_ANALYZE",
  	"VALIDATED",
  	"UNDER_WARRANTY"
] as const;

export const inProgressConstructionSiteStatuses: ConstructionSiteStatus[] = ["NEW", "IN_PROGRESS", "TO_ANALYZE","TO_RECEIVE"];

export const constructionSiteDocumentTypes = [
  "KBIS",
  "PIECE_IDENTITE",
  "PLANS",
  "DECLARATION_OUVERTURE_CHANTIER",
  "ATTESTATION_DEMARRAGE",
  "PERMIS_CONSTRUIRE",
  "DECLARATION_PREALABLE",
  "PLANNING_TRAVAUX",
  "DECLARATION_ACHEVEMENT_TRAVAUX",
  "CONVENTION_HONORAIRES",
  "ATTESTATION_DECENNALE",
  "FACTURE",
  "RAPPORT_ETUDE_TECHNIQUE",
  "RAPPORT_GEOTECHNIQUE",
  "RAPPORT_INITIAL_CONTROLE_TECHNIQUE",
  "RAPPORT_FINAL_CONTROLE_TECHNIQUE",
  "DEVIS",
  "PV_RECEPTION",
  "LEVEE_RESERVES_PV_RECEPTION",
  "REFERE_PREVENTIF_POUR_AVOISINANTS",
  "CONSTAT_HUISSIER_POUR_EXISTANTS",
  "CONTRAT_RESERVATION_BAILLEUR_SOCIAL",
  "INDICATION_TARIFAIRE",

  "NOTICE_DESCRIPTIVE",
  
	"OTHER",
] as const;

export const constructionSiteDocumentTypesThatNeedParticipantLinkToBeConsideredAsReferenced: ConstructionSiteDocumentType[] = [
	"ATTESTATION_DECENNALE",
	"CONVENTION_HONORAIRES",
	"DEVIS",
	"FACTURE",
	"PV_RECEPTION",
	"RAPPORT_ETUDE_TECHNIQUE",
	"RAPPORT_FINAL_CONTROLE_TECHNIQUE",
	"RAPPORT_GEOTECHNIQUE",
	"RAPPORT_INITIAL_CONTROLE_TECHNIQUE",
	"LEVEE_RESERVES_PV_RECEPTION"
] as const;

export const constructionSiteDocumentTypeLabels = {
	KBIS: "KBIS",
	PIECE_IDENTITE: "Pièce d'identité",
	PLANS: "Plans",
	DECLARATION_OUVERTURE_CHANTIER: "Déclaration d'ouverture de chantier",
	ATTESTATION_DEMARRAGE: "Attestation de démarrage",
	PERMIS_CONSTRUIRE: "Permis de construire",
	DECLARATION_PREALABLE: "Déclaration préalable",
	PLANNING_TRAVAUX: "Planning des travaux",
	DECLARATION_ACHEVEMENT_TRAVAUX: "Déclaration d'achèvement des travaux",
	CONVENTION_HONORAIRES: "Convention d'honoraires",
	ATTESTATION_DECENNALE: "Attestation décennale",
	FACTURE: "Facture",
	RAPPORT_ETUDE_TECHNIQUE: "Rapport d'étude technique",
	RAPPORT_GEOTECHNIQUE: "Rapport géotechnique",
	RAPPORT_INITIAL_CONTROLE_TECHNIQUE: "Rapport initial de contrôle technique",
	RAPPORT_FINAL_CONTROLE_TECHNIQUE: "Rapport final de contrôle technique",
	DEVIS: "Devis",
	PV_RECEPTION: "PV de réception",
	LEVEE_RESERVES_PV_RECEPTION: "Levée des réserves",
	REFERE_PREVENTIF_POUR_AVOISINANTS: "Référentiel préventif pour les avoisinants",
	CONSTAT_HUISSIER_POUR_EXISTANTS: "Constat d'huissier pour les existants",
	CONTRAT_RESERVATION_BAILLEUR_SOCIAL: "Contrat de réservation du bailleur social",
	INDICATION_TARIFAIRE: "Indication tarifaire",
	NOTICE_DESCRIPTIVE: "Notice descriptive",
	OTHER: "Autre",
} as const;


type BuildingDetailFieldValue = { 
	name: string; 
	indication?: string;
	label?: string;
};

type BuildingDetails = {
	formLabel: string;
	label: string;
	separator?: string;
	values: BuildingDetailFieldValue[];
};

type ActionTypeCheckbox = {
	label: string;
	value: ActionType;
	typeCheckboxesToHide?: ActionType[];
	buildingTypeCheckboxesToHide?: ActionBuildingType[];
};

type ActionBuildingTypeCheckbox = {
	label: string;
	value: ActionBuildingType;
	details?: BuildingDetails[];
	extensionDetails?: BuildingDetails[];
};

export const constructionSiteAbilityControlSelect = {
	status: true,
  	brokerSocietyId: true,
	syndicId: true,
	constructionSiteOffers: {
		select: {
		binderWarranty: {
			select: {
			binderAmendment: {
				select: {
				binder: {
					select: {
					insurer: {
						select: {
						societyId: true,
						},
					},
					},
				},
				},
			},
			},
		},
		},
	},
	constructionSiteClientDetails: {
		select: {
		clientSocietyId: true,
		},
	},
} satisfies Prisma.ConstructionSiteSelect

export const constructionSiteClientSyndicSocietyInfoSelector = {
	id: true,
	clientType: true,
} satisfies Prisma.ConstructionSiteClientDetailsSelect;

export const constructionSiteSyndicInfoSelector = {
	id: true,
	syndic: true,
	constructionSiteClientDetails: {
		select: constructionSiteClientSyndicSocietyInfoSelector,
	},
} satisfies Prisma.ConstructionSiteSelect;

export const constructionSiteClientDetailsSelector = {
	clientType: true,
	clientIsMOA: true,
	clientSociety: {
		select: clientSocietySelector,
	},
	constructionSiteClientContacts: {
		select: {
			constructionSiteContact: {
				select: clientUserSelector,
			},
		},
	},
	moaClientType: true,
	moaClientContact: {
		select: clientUserSelector,
	},
	moaClientSociety: {
		select: clientSocietySelector,
	},
} satisfies Prisma.ConstructionSiteClientDetailsSelect;

export const constructionSiteActionSelector = {
	id: true,
	types: true,
	buildingType: true,
	buildingUsage: true,
	complementaryTypes: true,
	complementaryBuildingType: true,
	fromFloor: true,
	toFloor: true,
	surface: true,
	extensionSurface: true,
	floorsCount: true,
	basementsCount: true,
	habitationsCount: true,
	buildingsCount: true,
	maxFloors: true,
	maxBasements: true,
	habitationsSize: true,
	officesSize: true,
	commercialBuildingsSize: true,
	underpinningExistingFoundations: true,
	interventionOnLoadBearingStructure: true,
	socialLandlordRental: true,
} satisfies Prisma.ConstructionSiteActionSelect;

export const constructionSiteCreationDetailsSelector = {
	address: { select: { streetNumber: true, streetName: true, postalCode: true, city: true, department: true } },
	rga: true,
	sismic: true,
	startDate: true,
	deliveryDate: true,
	cityHallStartDate: true,
	interventions: true,
	ccmi: true,
	nonUsualMethods: true,
	noOtherOperations: true,
	totalAmountTTC: true,
	totalAmountHT: true,
	constructionSiteAction: { select: constructionSiteActionSelector },
} satisfies Prisma.ConstructionSiteDetailsSelect;


export const constructionSiteCreationSelector = {
	id: true,
	brokerSociety: {
		select: {
			brokerDetails: {
				select: {
					accessibleProduct: true,
				},
			},
			managingBrokerSociety: {
				select: {
					brokerDetails: {
						select: {
							accessibleProduct: true,
						},
					},
				},
			},
		},
	},
	syndic: {
		select: {
			id: true,
			siren: true,
			denomination: true,
			brokerageFees: true,
			syndicUserAccessToPlatform: {
				select: {
					firstname: true,
					lastname: true,
					email: true,
					role: true,
				},
			},
		},
	},
	constructionSiteClientDetails: { select: constructionSiteClientSyndicSocietyInfoSelector },
	constructionSiteDetails: { select: constructionSiteCreationDetailsSelector },
	constructionSiteOffers: {
		select: constructionSiteOfferSelector,
	}
} satisfies Prisma.ConstructionSiteSelect;

export const constructionSiteDetailsSelector = 
	{
		id: true,
		address: {
			select: {
				id: true,
				city: true,
				streetName: true,
				streetNumber: true,
				postalCode: true,
			},
		},
		rga: true,
		sismic: true,
		taxMode: true,
		totalAmountTTC: true,
		totalAmountHT: true,
		finalTotalAmount: true,
		startDate: true,
		deliveryDate: true,
		cityHallStartDate: true,
		receptionDate: true,
		interventions: true,
		ccmi: true,
		nonUsualMethods: true,
		noOtherOperations: true,
		constructionSiteAction: {
			select: constructionSiteActionSelector
		},
} satisfies Prisma.ConstructionSiteDetailsSelect;

export const baseProjectSelector = {
	id: true,
	emailRoutingUuid: true,
	status: true,
	skipReason: true,
	isSkipped: true,
	skippedAt: true,
	createdAt: true,
	updatedAt: true,
	constructionSiteClientDetails: {
		select: constructionSiteClientDetailsSelector,
	},
	syndic: {
		select: {
			id: true,
			siren: true,
			denomination: true,
			brokerageFees: true,
			syndicUserAccessToPlatform: {
				select: {
					firstname: true,
					lastname: true,
					email: true,
					role: true,
				},
			},
			headOfficeAddress: {
				select: {
					streetNumber: true,
					streetName: true,
					postalCode: true,
					city: true,
				},
			},
		},
	},
	propositions: {
		select: {
			id: true,
			reference: true,
			primeTTC: true,
			remunerationApporteur: true,
			status: true,
			productType: true,
		},
	},
  } satisfies Prisma.ConstructionSiteSelect;

export const constructionSiteWithDetailsSelector = 
  {
	...baseProjectSelector,
	constructionSiteDetails: {
		select: constructionSiteDetailsSelector,
	},
	brokerSociety: {
		select: brokerSocietyBaseSelector,
	},
  } satisfies Prisma.ConstructionSiteSelect;

export const constructionSiteFullDetailsSelector = {
	...constructionSiteWithDetailsSelector,
	constructionSiteOffers: {
		select: constructionSiteOfferSelector,
	}
} satisfies Prisma.ConstructionSiteSelect;

export const constructionSiteDocumentSelect = {
	id: true,
	type: true,
	createdAt: true,
	updatedAt: true,
	File: {
		select: {
			id: true,
			name: true,
			bucket: true,
			key: true,
			mimeType: true,
			humanProcessingStatus: true,
			createdAt: true,
			updatedAt: true,
			author: {
				select: {
					firstname: true,
					lastname: true,
					role: true,
					subrole: true,
				},
			},
		},
	},
} satisfies Prisma.FilesOnConstructionSiteSelect;

export const constructionSiteWithTasksSelector = {
	id: true,
	status: true,
	createdAt: true,
	updatedAt: true,
	constructionSiteDetails: {
		select: {
			id: true,
			address: {
				select: {
					city: true,
					streetName: true,
					streetNumber: true,
					postalCode: true,
				},
			},
		},
	},
	constructionSiteClientDetails: {
		select: {
			id: true,
			clientType: true,
			clientSociety: {
				select: {
					denomination: true,
				},
			},
			constructionSiteClientContacts: {
				select: {
					constructionSiteContact: {
						select: {
							firstname: true,
							lastname: true,
						},
					},
				},
			},
		},
	},
} satisfies Prisma.ConstructionSiteSelect;

export type ConstructionSiteWithTasks = Prisma.ConstructionSiteGetPayload<{
	select: typeof constructionSiteWithTasksSelector;
}>;

export const constructionSiteIdSelect = {
	id: true,
} satisfies Prisma.ConstructionSiteSelect;

export const ACTION_TYPES = {
	CONSTRUCTION: "la construction",
	RENOVATION: "la rénovation",
	EXTENSION: "l'extension",
}
type ActionType = keyof typeof ACTION_TYPES;

export const ACTION_BUILDING_TYPES = {
	COMMON_PARTS: "des parties communes",
	PRIVATE_WORK: "des travaux privatifs",
	APPARTMENT: "d'un appartement",
	INDIVIDUAL_HOUSE: "d'une maison individuelle",
	LIVING_BUILDING: "d'un immeuble à usage d'habitation",
	COMMERCIAL_BUILDING: "d'un bâtiment commercial",
	LOT_HOUSES:
		"de maisons individuelles en bande ou d'une opération pavillonnaire",
	OFFICE_BUILDING: "d'un immeuble à usage de bureaux",
	MIXT_BUILDING: "d'un bâtiment à usage mixte",
};
export type ActionBuildingType = keyof typeof ACTION_BUILDING_TYPES;

export const RENOVATION_OR_EXTENSION_ACTION_TYPES = {
	underpinningExistingFoundations:
		"la reprise en sous oeuvre des fondations existantes",
	interventionOnLoadBearingStructure:
		"l'intervention sur la structure porteuse",
} as const;

export const BUILDING_USAGE_TYPES = {
	LIVING: "Habitation",
	COMMERCIAL_TERTIARY: "Commerce/Tertiaire",
	MIXT: "Mixte",
} as const;

export const actionTypeCheckboxes: ActionTypeCheckbox[] = [
	{
		label: ACTION_TYPES.CONSTRUCTION,
		value: "CONSTRUCTION",
		typeCheckboxesToHide: ["RENOVATION", "EXTENSION"],
		buildingTypeCheckboxesToHide: ["APPARTMENT"],
	},
	{
		label: ACTION_TYPES.RENOVATION,
		value: "RENOVATION",
		typeCheckboxesToHide: ["CONSTRUCTION"],
	},
	{
		label: ACTION_TYPES.EXTENSION,
		value: "EXTENSION",
		typeCheckboxesToHide: ["CONSTRUCTION"],
	},
];

const BASE_BUILDING_DETAILS: BuildingDetails[] = [
	{
		label: "Comprenant",
		formLabel: "comprenant",
		values: [{ name: "buildingsCount", indication: "bâtiment(s)" }],
	},
	{
		formLabel: "de",
		label: "de",
		values: [
			{ name: "maxFloors", indication: "étage(s) maximum" },
			{ name: "maxBasements", indication: "sous-sol(s) maximum" },
		],
		separator: "et",
	},
	{
		formLabel: "La surface totale du bâtiment est de",
		label: "La surface totale du bâtiment est de",
		values: [{ name: "surface", indication: "m²" }],
	},
];

const BASE_BUILDING_DETAILS_WITH_EXTENSION: BuildingDetails[] = [
	{
		label: "Comprenant",
		formLabel: "comprenant *",
		values: [{ name: "buildingsCount", indication: "bâtiment(s)" }],
	},
	{
		formLabel: "de",
		label: "de",
		values: [
			{ name: "maxFloors", indication: "étage(s) maximum" },
			{ name: "maxBasements", indication: "sous-sol(s) maximum" },
		],
		separator: "et",
	},
	{
		formLabel: "L'extension et surface totale *",
		label: "Extension et surface",
		values: [
			{ name: "extensionSurface", indication: "m²", label: "L'extension créé" },
			{ name: "surface", indication: "m²", label: "pour une surface totale du bâtiment de" }
		],
	},
];

const BUILDING_WITH_HABITATION_DETAILS: BuildingDetails[] = [
	...BASE_BUILDING_DETAILS,
	{
		formLabel: "L'opération concerne",
		label: "L'opération concerne",
		values: [{ name: "habitationsCount", indication: "logement(s)" }],
	},
];

const BUILDING_WITH_HABITATION_DETAILS_WITH_EXTENSION: BuildingDetails[] = [
	...BASE_BUILDING_DETAILS_WITH_EXTENSION,
	{
		formLabel: "L'opération concerne *",
		label: "L'opération concerne",
		values: [{ name: "habitationsCount", indication: "logement(s)" }],
	},
];

export const commonPartsActionBuildingTypeCheckboxes: ActionBuildingTypeCheckbox = 
	{
		label: ACTION_BUILDING_TYPES.COMMON_PARTS,
		value: "COMMON_PARTS",
		details: BASE_BUILDING_DETAILS,
	}

export const privateWorkActionBuildingTypeCheckboxes: ActionBuildingTypeCheckbox = 
	{
		label: ACTION_BUILDING_TYPES.PRIVATE_WORK,
		value: "PRIVATE_WORK",
		details: [
			{
				formLabel: "situé à l'étage",
				label: "Situé à l'étage",
				values: [{ name: "fromFloor" }, { name: "toFloor" }],
				separator: "sur",
			},
			{
				formLabel: "La surface totale de l'appartement est de",
				label: "La surface totale de l'appartement est de",
				values: [{ name: "surface", indication: "m²" }],
			},
		],
	}

export const actionBuildingTypeCheckboxes: ActionBuildingTypeCheckbox[] = [
	{
		label: ACTION_BUILDING_TYPES.APPARTMENT,
		value: "APPARTMENT",
		details: [
			{
				formLabel: "situé à l'étage",
				label: "Situé à l'étage",
				values: [{ name: "fromFloor" }, { name: "toFloor" }],
				separator: "sur",
			},
			{
				formLabel: "La surface totale de l'appartement est de",
				label: "La surface totale de l'appartement est de",
				values: [{ name: "surface", indication: "m²" }],
			},
		],
		extensionDetails: [
			{
				formLabel: "situé à l'étage *",
				label: "Situé à l'étage",
				values: [{ name: "fromFloor" }, { name: "toFloor" }],
				separator: "sur",
			},
			{
				formLabel: "L'extension et surface totale *",
				label: "Extension et surface",
				values: [
					{ name: "extensionSurface", indication: "m²", label: "L'extension créé" },
					{ name: "surface", indication: "m²", label: "pour une surface totale de l'appartement de" }
				],
			},
		],
	},
	{
		label: ACTION_BUILDING_TYPES.INDIVIDUAL_HOUSE,
		value: "INDIVIDUAL_HOUSE",
		details: [
			{
				formLabel: "comprenant",
				label: "Comprenant",
				values: [
					{ name: "floorsCount", indication: "étage(s)(R+)" },
					{ name: "basementsCount", indication: "sous-sol(s)(R-)" },
				],
				separator: "et",
			},
			{
				formLabel: "La surface totale du bâtiment est de",
				label: "La surface totale du bâtiment est de",
				values: [{ name: "surface", indication: "m²" }],
			},
			{
				formLabel: "L'opération concerne",
				label: "L'opération concerne",
				values: [{ name: "habitationsCount", indication: "logement(s)" }],
			},
		],
		extensionDetails: [
			{
				formLabel: "comprenant *",
				label: "Comprenant",
				values: [
					{ name: "floorsCount", indication: "étage(s)(R+)" },
					{ name: "basementsCount", indication: "sous-sol(s)(R-)" },
				],
				separator: "et",
			},
			{
				formLabel: "L'extension et surface totale *",
				label: "Extension et surface",
				values: [
					{ name: "extensionSurface", indication: "m²", label: "L'extension créé" },
					{ name: "surface", indication: "m²", label: "pour une surface totale du bâtiment de" }
				],
			},
			{
				formLabel: "L'opération concerne *",
				label: "L'opération concerne",
				values: [{ name: "habitationsCount", indication: "logement(s)" }],
			},
		],
	},
	{
		label: ACTION_BUILDING_TYPES.LIVING_BUILDING,
		value: "LIVING_BUILDING",
		details: BUILDING_WITH_HABITATION_DETAILS,
		extensionDetails: BUILDING_WITH_HABITATION_DETAILS_WITH_EXTENSION,
	},
	{
		label: ACTION_BUILDING_TYPES.LOT_HOUSES,
		value: "LOT_HOUSES",
		details: BUILDING_WITH_HABITATION_DETAILS,
		extensionDetails: BUILDING_WITH_HABITATION_DETAILS_WITH_EXTENSION,
	},
	{
		label: ACTION_BUILDING_TYPES.OFFICE_BUILDING,
		value: "OFFICE_BUILDING",
		details: BASE_BUILDING_DETAILS,
		extensionDetails: BASE_BUILDING_DETAILS_WITH_EXTENSION,
	},
	{
		label: ACTION_BUILDING_TYPES.COMMERCIAL_BUILDING,
		value: "COMMERCIAL_BUILDING",
		details: BASE_BUILDING_DETAILS,
		extensionDetails: BASE_BUILDING_DETAILS_WITH_EXTENSION,
	},
	{
		label: ACTION_BUILDING_TYPES.MIXT_BUILDING,
		value: "MIXT_BUILDING",
		details: [
			...BUILDING_WITH_HABITATION_DETAILS,
			{
				formLabel: "et se décompose en :",
				label: "Et se décompose en",
				separator: "et",
				values: [
					{ name: "habitationsSize", indication: "m² de logements" },
					{ name: "officesSize", indication: "m² de bureaux" },
					{ name: "commercialBuildingsSize", indication: "m² de commerces" },
				],
			},
		],
		extensionDetails: [
			...BUILDING_WITH_HABITATION_DETAILS_WITH_EXTENSION,
			{
				formLabel: "et se décompose en : *",
				label: "Et se décompose en",
				values: [
					{ name: "habitationsSize", indication: "m² de logements" },
					{ name: "officesSize", indication: "m² de bureaux" },
					{ name: "commercialBuildingsSize", indication: "m² de commerces" },
				],
			},
		],
	},
];
